import { useState } from 'react';
import { useAttentePieces } from '@/hooks/useInterventions';
import RepriseDialog from '@/components/RepriseDialog';
import { useAuthStore } from '@/stores/authStore';
import { formatJourMur } from '@/lib/dates';
import type { AttentePiecesItem } from '@/lib/types';

/** Age in days since the intervention entered ATTENTE_PIECES (wall-clock). */
function joursDepuis(iso: string | null): string {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days === 0 ? "aujourd'hui" : `${days} j`;
}

export default function AttentePiecesPage() {
  const { data, isLoading, isError } = useAttentePieces();
  const [selected, setSelected] = useState<AttentePiecesItem | null>(null);
  const user = useAuthStore((s) => s.user);
  const canReprise = user?.role === 'admin' || user?.role === 'atelier' || user?.role === 'reception';
  const items = data ?? [];

  return (
    <div className="p-6 space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-text-primary">
          Attente pièces{' '}
          {items.length > 0 && (
            <span className="text-text-muted">({items.length})</span>
          )}
        </h1>
        <p className="text-xs text-text-muted mt-0.5">
          Motos en attente de pièces — triées de la plus ancienne à la plus récente
        </p>
      </header>

      {isLoading && <p className="text-sm text-text-muted">Chargement…</p>}

      {isError && (
        <div className="px-4 py-3 rounded-md bg-status-annule-bg text-status-annule-text text-sm">
          Erreur lors du chargement des motos en attente de pièces
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div className="text-center py-16 bg-surface-white border border-border-light rounded-lg">
          <p className="text-text-muted text-sm">
            Aucune moto en attente de pièce.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <section className="bg-surface-white border border-border-light rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-bg">
              <tr className="text-xs uppercase tracking-wider text-text-muted">
                <th className="text-left px-4 py-2.5">Client</th>
                <th className="text-left px-4 py-2.5">Moto</th>
                <th className="text-left px-4 py-2.5">En attente depuis</th>
                <th className="text-left px-4 py-2.5">Livraison estimée</th>
                <th className="text-left px-4 py-2.5">Mécano</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-t border-surface-divider hover:bg-surface-hover"
                >
                  <td className="px-4 py-3 font-medium text-text-primary">
                    {item.client.prenom} {item.client.nom}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">
                    {item.moto.modele}
                  </td>
                  <td className="px-4 py-3 font-medium text-status-attente-pieces-text">
                    {joursDepuis(item.attentePiecesDepuis)}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">
                    {item.livraisonEstimee ? formatJourMur(item.livraisonEstimee) : '—'}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">
                    {item.mecanicien.prenom}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canReprise && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setSelected(item)}
                      >
                        Reprendre
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {selected && (
        <RepriseDialog
          intervention={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
